import React, { useState, useEffect, useRef } from 'react';
import Navbar from '../layout/Navbar';
import Footer from '../layout/Footer';
import { Clock, Mail, MapPin, Phone } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';

const Contact = ({ showFooter = true }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        product: '',
        company: '',
        message: ''
    });

    const [errors, setErrors] = useState({});
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [showProductDropdown, setShowProductDropdown] = useState(false);
    const dropdownRef = useRef(null);

    const productOptions = [
        'Raw Materials',
        'Paper Cup Fans',
        'Single Wall Cups',
        'Double Wall Cups',
        'Ripple Cups',
        'Paper Containers',
        'Paper Lids',
        'Paper Trays',
        'Pizza Boxes',
        'Food Boxes',
        'Burger Boxes'

    ];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });

        if (errors[name]) {
            setErrors({ ...errors, [name]: false });
        }
    };

    const toggleProduct = (product) => {
        if (selectedProducts.includes(product)) {
            setSelectedProducts(selectedProducts.filter((p) => p !== product));
        } else {
            setSelectedProducts([...selectedProducts, product]);
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = "Name is required";
        if (!formData.email.trim()) newErrors.email = "Email is required";
        if (!formData.phone.trim()) newErrors.phone = "Phone is required";
        if (selectedProducts.length === 0) newErrors.products = "Please select at least one product";


        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        const { name, email, phone, company, message } = formData;
        const subject = encodeURIComponent(`New Contact Form ${name}`);
        const body = encodeURIComponent(
            `Name: ${name}\nEmail: ${email}\nPhone: ${phone}\nProducts: ${selectedProducts.join(', ')}\nCompany: ${company}\nMessage: ${message}`
        );

        const gmailLink = `https://mail.google.com/mail/?view=cm&fs=1&to=revawkr@gmail.com&su=${subject}&body=${body}`;
        window.open(gmailLink, "_blank");

        setFormData({ name: '', email: '', phone: '', product: '', company: '', message: '' });
        setSelectedProducts([]);
        setErrors({});
    };

    const inputClass = (field) =>
        `w-full p-3 rounded bg-[rgba(255,255,255,0.9)] text-[#333] outline-none ${errors[field] ? 'border-2 border-red-500' : 'border-none'
        }`;

    // Handle outside click for product dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowProductDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <>
            <Navbar />
            <section id="contact" className="bg-gray-100 py-20">
                <div className="container mx-auto px-5">
                    <h2 className="text-center text-4xl mb-16 text-[#2c5530] font-bold relative after:content-[''] after:absolute after:bottom-[-10px] after:left-1/2 after:-translate-x-1/2 after:w-24 after:h-1 after:bg-gradient-to-r after:from-[#ff6b6b] after:to-[#ffa500] after:rounded">
                        Contact Us
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
                        <div className="bg-gray-300 p-8 rounded-lg backdrop-blur-md">
                            <h3 className="text-2xl mb-4">Get a Quote</h3>
                            <div className="mb-6">
                                <label htmlFor="name" className="block mb-2 font-bold">Name <span className='text-red-700'>*</span></label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    className={inputClass('name')}
                                    placeholder="Enter Full Name"
                                    required
                                />
                                {errors.name && (
                                    <p className='text-red-500 text-md mt-1'>{errors.name}</p>
                                )}
                            </div>
                            <div className="mb-6">
                                <label htmlFor="email" className="block mb-2 font-bold">Email <span className='text-red-700'>*</span></label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    className={inputClass('email')}
                                    placeholder="Enter Email Id"
                                    required
                                />
                                {errors.email && (
                                    <p className='text-red-500 text-md mt-1'>{errors.email}</p>
                                )}
                            </div>

                            {/* Phone + Product Section */}
                            <div className="mb-6 flex flex-col md:flex-row gap-4">
                                <div className="w-full md:w-1/2">
                                    <label htmlFor="phone" className="block mb-2 font-bold">Phone <span className='text-red-700'>*</span></label>
                                    <input
                                        type="tel"
                                        id="phone"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        className={inputClass('phone')}
                                        placeholder="Enter Phone Number"
                                        required
                                    />
                                    {errors.phone && (
                                        <p className='text-red-500 text-md mt-1'>{errors.phone}</p>
                                    )}
                                </div>

                                <div className="w-full md:w-1/2 relative" ref={dropdownRef}>
                                    <label className="block mb-2 font-bold">Interested Products</label>
                                    <div
                                        className="w-full p-3 rounded bg-[rgba(255,255,255,0.9)] text-[#333] border cursor-pointer"
                                        onClick={() => setShowProductDropdown(!showProductDropdown)}
                                    >
                                        <span className={selectedProducts.length === 0 ? 'text-gray-400' : ''}>
                                            {selectedProducts.length > 0
                                                ? selectedProducts.join(', ')
                                                : 'Select Products'}
                                        </span>
                                    </div>

                                    {showProductDropdown && (
                                        <div className="absolute z-10 mt-2 w-full max-h-40 overflow-y-auto bg-white border rounded shadow">
                                            {productOptions.map((product) => (
                                                <label key={product} className="flex items-center px-3 py-2 hover:bg-gray-100">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedProducts.includes(product)}
                                                        onChange={() => toggleProduct(product)}
                                                        className="mr-2"
                                                    />
                                                    {product}
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                    {errors.products && (
                                        <p className="text-red-500 text-sm mt-2">{errors.products}</p>
                                    )}

                                </div>
                            </div>

                            <div className="mb-6">
                                <label htmlFor="company" className="block mb-2 font-bold">Company</label>
                                <input
                                    type="text"
                                    id="company"
                                    name="company"
                                    value={formData.company}
                                    onChange={handleInputChange}
                                    className="w-full p-3 border-none rounded bg-[rgba(255,255,255,0.9)] text-[#333] outline-none"
                                    placeholder="Enter Company Name"
                                />
                            </div>

                            <div className="mb-6">
                                <label htmlFor="message" className="block mb-2 font-bold">Message</label>
                                <textarea
                                    id="message"
                                    name="message"
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    className="w-full p-3 border-none rounded bg-[rgba(255,255,255,0.9)] text-[#333] h-32 resize-y outline-none"
                                    placeholder="Tell us about your requirements..."
                                ></textarea>
                            </div>

                            <button
                                onClick={handleSubmit}
                                className="bg-gradient-to-r from-[#ff6b6b] to-[#ffa500] text-white py-3 px-7 border-none rounded-full font-bold hover:-translate-y-0.5 transition-transform"
                            >
                                Send Message
                            </button>
                        </div>

                        {/* Contact Info */}
                        <div className="p-8">
                            <h3 className="text-2xl mb-4">Get in Touch</h3>
                            <div className="mb-8 flex items-center gap-4">
                                <div className="text-2xl text-[#ff6b6b]"><MapPin /></div>
                                <div>
                                    <strong>Address:</strong><br />
                                    Reva Industries, NH-27, Lalpar,<br />
                                    Wankaner, Morbi, Gujarat 363621<br />
                                    India.
                                </div>
                            </div>
                            <div className="mb-8 flex items-center gap-4">
                                <div className="text-2xl text-[#ff6b6b]"><Phone /></div>
                                <div>
                                    <strong>Phone:</strong><br />
                                    +91 94267 88499<br />
                                    +91 94089 39938<br />
                                    +91 63523 32005
                                </div>
                            </div>
                            <div className="mb-8 flex items-center gap-4">
                                <div className="text-2xl text-[#ff6b6b]"><Mail /></div>
                                <div>
                                    <strong>Email:</strong><br />
                                    revawkr@gmail.com
                                </div>
                            </div>
                            <div className="mb-8 flex items-center gap-4">
                                <div className="text-2xl text-[#ff6b6b]"><Clock /></div>
                                <div>
                                    <strong>Business Hours:</strong><br />
                                    Monday - Friday: 9:00 AM - 6:00 PM<br />
                                    Saturday: 9:00 AM - 2:00 PM
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            {showFooter && <Footer />}
            <div className="fixed bottom-5 right-5 flex flex-col gap-3 z-20">
                <a
                    href="tel:+919426788499"
                    className={`bg-sky-500 text-white w-12 h-12 rounded-lg text-xl flex items-center justify-center cursor-pointer hover:bg-sky-700 transition-all`}
                    title="Call Us"
                >
                    <Phone />
                </a>
                <a
                    href="https://wa.me/+919426788499"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`bg-[#3ed145] text-white w-12 h-12 rounded-lg text-xl flex items-center justify-center cursor-pointer hover:bg-[#2f8734] transition-all`}
                    title="Message on WhatsApp"
                >
                    <FaWhatsapp className='h-12 w-8' />
                </a>
            </div>
        </>
    );
};

export default Contact;
